import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleHelp, CreditCard, FileText, GitMerge, KeyRound, Mail, Receipt, StickyNote, Tag, Workflow } from "lucide-react";
import type { Contact } from "@/lib/types";
import { ApiError } from "@/lib/api";
import { hasOp, pendingModules } from "@/lib/stubs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Tip } from "@/components/ui/tooltip";
import { OPEN_TAG_PICKER, useAddNote, useMerge } from "./hooks";
import { LoginLinkDialog, SendEmailDialog, StartAutomationDialog } from "./ActionDialogs";
import { InvoiceDialog, TakePaymentDialog } from "../billing/BillingDialogs";
import { SignInExplainDialog } from "./SignInExplainDialog";

/** Which action-bar operations this server has (other streams' modules land over time). */
function useOps(names: string[]) {
  return useQuery({
    queryKey: ["has-ops", names.join(",")],
    queryFn: async () => Object.fromEntries(await Promise.all(names.map(async (n) => [n, await hasOp(n)] as const))) as Record<string, boolean>,
    staleTime: Infinity,
  });
}

/** Action bar: note, email, tag, automation, payment/order, invoice/pay link, login link, "why can't they sign in?", merge. */
export function ActionBar({ c }: { c: Contact }) {
  const [dialog, setDialog] = useState<"note" | "merge" | "email" | "automation" | "login" | "explain" | "payment" | "invoice" | null>(null);
  const ops = useOps(["email_send_one", "automations_enroll", "logins_link_send", "logins_explain", "orders_create", "invoices_create"]).data ?? {};
  const archivedTip = c.archived ? "Archived contacts are reference only" : undefined;
  const qc = useQueryClient();
  // Billing's dialogs refresh the timeline; the header (lifetime value, orders) and billing panels are refreshed here.
  const closeBilling = () => {
    setDialog(null);
    for (const key of ["contact", "billing-summary", "billing-detail"]) void qc.invalidateQueries({ queryKey: [key, c.id] });
  };
  const pending = (label: string, icon: ReactNode, stream: string) => (
    <Tip text={`Arrives with the ${stream} module`}>
      <span>
        <Button variant="outline" size="sm" disabled>
          {icon}
          {label}
        </Button>
      </span>
    </Tip>
  );
  return (
    <div className="flex flex-wrap gap-1.5">
      <Button variant="outline" size="sm" onClick={() => setDialog("note")}>
        <StickyNote /> Note
      </Button>
      {ops.email_send_one ? (
        <Button variant="outline" size="sm" onClick={() => setDialog("email")} disabled={c.archived || c.emails.length === 0} title={c.archived ? "Archived contacts are never emailed" : undefined}>
          <Mail /> Email
        </Button>
      ) : (
        pending("Email", <Mail />, pendingModules.email)
      )}
      <Button variant="outline" size="sm" onClick={() => window.dispatchEvent(new Event(OPEN_TAG_PICKER))}>
        <Tag /> Tag
      </Button>
      {ops.automations_enroll ? (
        <Button variant="outline" size="sm" onClick={() => setDialog("automation")} disabled={c.archived}>
          <Workflow /> Start automation
        </Button>
      ) : (
        pending("Start automation", <Workflow />, pendingModules.automations)
      )}
      {ops.orders_create ? (
        <Button variant="outline" size="sm" onClick={() => setDialog("payment")} disabled={c.archived} title={archivedTip}>
          <CreditCard /> Take payment / new order
        </Button>
      ) : (
        pending("Take payment / new order", <CreditCard />, pendingModules.billing)
      )}
      {ops.invoices_create ? (
        <Button variant="outline" size="sm" onClick={() => setDialog("invoice")} disabled={c.archived} title={archivedTip}>
          <Receipt /> Invoice / pay link
        </Button>
      ) : (
        pending("Invoice / pay link", <Receipt />, pendingModules.billing)
      )}
      {ops.logins_link_send ? (
        <Button variant="outline" size="sm" onClick={() => setDialog("login")} disabled={c.archived}>
          <KeyRound /> Send login link
        </Button>
      ) : (
        pending("Send login link", <KeyRound />, pendingModules.logins)
      )}
      {ops.logins_explain && (
        <Button variant="outline" size="sm" onClick={() => setDialog("explain")}>
          <CircleHelp /> Why can't they sign in?
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={() => setDialog("merge")}>
        <GitMerge /> Merge
      </Button>
      <NoteDialog c={c} open={dialog === "note"} onClose={() => setDialog(null)} />
      <MergeDialog c={c} open={dialog === "merge"} onClose={() => setDialog(null)} />
      {dialog === "email" && <SendEmailDialog c={c} onClose={() => setDialog(null)} />}
      {dialog === "automation" && <StartAutomationDialog c={c} onClose={() => setDialog(null)} />}
      {dialog === "login" && <LoginLinkDialog c={c} onClose={() => setDialog(null)} />}
      {dialog === "explain" && <SignInExplainDialog contactId={c.id} onClose={() => setDialog(null)} />}
      {dialog === "payment" && <TakePaymentDialog contactId={c.id} onClose={closeBilling} />}
      {dialog === "invoice" && <InvoiceDialog contactId={c.id} defaultBrandId={c.brands[0]?.brand_id} onClose={closeBilling} />}
    </div>
  );
}

function NoteDialog({ c, open, onClose }: { c: Contact; open: boolean; onClose: () => void }) {
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("note");
  const [pinned, setPinned] = useState(false);
  const add = useAddNote(c.id);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>
          <FileText className="mr-1 inline size-4" /> Add a note
        </DialogTitle>
        <DialogDescription>It goes on the timeline. Notes are visible to everyone who can open this contact.</DialogDescription>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate({ body, kind, pinned }, { onSuccess: () => (setBody(""), setPinned(false), onClose()) });
          }}
        >
          <div className="flex gap-1" role="radiogroup" aria-label="What kind of note">
            {[
              ["note", "Note"],
              ["call", "Phone call"],
              ["meeting", "Meeting"],
              ["email", "Email"],
            ].map(([k, label]) => (
              <Button key={k} type="button" size="sm" role="radio" aria-checked={kind === k} variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)}>
                {label}
              </Button>
            ))}
          </div>
          <Textarea autoFocus aria-label="Note text" value={body} onChange={(e) => setBody(e.target.value)} placeholder="What happened?" required />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} className="accent-[var(--color-primary)]" />
            Pin it to the top of the contact
          </label>
          {add.error && <p className="text-sm text-[var(--color-destructive)]">{(add.error as ApiError).message}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={add.isPending || !body.trim()}>
              Save note
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MergeDialog({ c, open, onClose }: { c: Contact; open: boolean; onClose: () => void }) {
  const [loser, setLoser] = useState("");
  const [reason, setReason] = useState("");
  const merge = useMerge(c.id);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>Merge a duplicate into #{c.id}</DialogTitle>
        <DialogDescription>
          Both records are saved in a note first. This contact's values win; the duplicate's emails, tags, orders and history move here, and its ID keeps
          working.
        </DialogDescription>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            merge.mutate({ loserId: Number(loser), reason }, { onSuccess: () => (setLoser(""), setReason(""), onClose()) });
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="loser">Duplicate contact ID</Label>
            <Input id="loser" inputMode="numeric" value={loser} onChange={(e) => setLoser(e.target.value.replace(/\D/g, ""))} required />
          </div>
          <div className="space-y-1">
            <Label htmlFor="reason">Why they're the same person</Label>
            <Input id="reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Same email" />
          </div>
          {merge.error && <p className="text-sm text-[var(--color-destructive)]">{(merge.error as ApiError).message}</p>}
          <div className="flex justify-end">
            <Button type="submit" variant="destructive" disabled={merge.isPending || !loser || Number(loser) === c.id}>
              Merge #{loser || "…"} into #{c.id}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
